import { cn } from "@/lib/utils"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-xl text-sm font-medium ring-offset-background transition-color focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary-500 text-white hover:bg-primary-400 inset-shadow-2xs inset-shadow-white/15 ring ring-primary shadow",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90 inset-shadow-2xs inset-shadow-white/15 ring ring-destructive shadow",
        outline: "border border-input bg-background hover:bg-background hover:text-accent-foreground inset-shadow-2xs inset-shadow-white/15 ring ring-background shadow",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80 inset-shadow-2xs inset-shadow-white/15 ring ring-gray-200 shadow",
        tertiary: "bg-tertiary text-tertiary-foreground hover:bg-tertiary/80 inset-shadow-2xs inset-shadow-white/15 ring ring-tertiary shadow",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 md:rounded-lg rounded-md px-3",
        lg: "h-10 md:rounded-lg rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  pill?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild = false, pill = false, ...props }, ref) => {
  const Comp = asChild ? Slot : "button"
  return <Comp className={cn(buttonVariants({ variant, size, className }), pill && "rounded-full")} ref={ref} {...props} />
})
Button.displayName = "Button"

export { Button, buttonVariants }
